
import { FSXABaseComponent, FSXARichText } from 'fsxa-pattern-library'
import { Component, Watch } from 'nuxt-property-decorator'
import { Option, Section } from 'fsxa-api'
import TrackingContainer from '../tracking/TrackingContainer.vue'
import TheCookieBanner from '../TheCookieBanner.vue'
import { getIndexPageUrl } from '../../middleware/RedirectsHandler'
import TheGeoTargeting, { IGeoTargetingInfo } from '../layouts/TheGeoTargeting.vue'
import Paragraph from './richtext/Paragraph.vue'
import { ILink } from '../../shared/general/interfaces/ILink'
import getLinkObject from '../../shared/fsxa/services/LinkService'
import TheModal from '../layouts/TheModal.vue'
import ImageVideoModalContent from '../ImageVideo/ImageVideoModalContent.vue'
import LightboxCarousel from '../LightboxCarousel.vue'
import TheJubileeFlyIn from '../TheJubileeFlyIn.vue'

interface ITrackingToolData {
  data ?: {
    st_trackingId ?: string
    st_trackingTool ?: {
      key ?: string
    }
  }
}

@Component({
  name: 'PageOverlays',
  components: {
    TheJubileeFlyIn,
    TheModal,
    TheGeoTargeting,
    TheCookieBanner,
    TrackingContainer,
    ImageVideoModalContent, // used in modal component
    LightboxCarousel, // used in modal component
  },
})
export default class PageOverlays extends FSXABaseComponent {
  private showGeoTargeting : boolean = false

  private geoTargetingInfo : IGeoTargetingInfo | false = false

  async mounted () {
    this.checkGeoTargeting()
  }

  private get isLandingPage () : boolean {
    return this.$store.state.Page?.pageType === 'LandingPage'
  }

  private get trackingTools () : Record<string, string> {
    const trackingTools = this.globalSettings?.data.ps_country_project_settings?.pt_tracking_tool
    return trackingTools?.reduce((result : Record<string, string>, { data } : ITrackingToolData) => ({
      ...result,
      [data?.st_trackingTool?.key || 'NO_KEY_GIVEN']: data?.st_trackingId,
    }), {})
  }

  private async checkGeoTargeting () : Promise<void> {
    // check if we have to show the geo targeting layer
    // if geoTargeting is present and has either the value no-redirect or redirect we don't show the layer

    const geoTargeting = localStorage.getItem('geoTargeting')
    if (geoTargeting === 'no-redirect') return
    if (this.$route.path !== await getIndexPageUrl(this.fsxaApi, this.$store.state.Locale.locale)) return

    this.geoTargetingInfo = this.getGeoTargetingInfo()

    if (!this.geoTargetingInfo.matchedCountry || !this.geoTargetingInfo.goToLink) return

    if (geoTargeting === 'redirect') {
      // redirect the user to the given country URL
      window.location.href = this.geoTargetingInfo.goToLink
      return
    }

    // if the user is in a country which we redirect to
    this.showGeoTargeting = !!this.geoTargetingInfo.matchedCountry
  }

  private getGeoTargetingInfo () : IGeoTargetingInfo {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

    const matchedCountry = this.globalSettings?.data?.ps_geotargeted_countries
      ?.filter((country : Section) => country?.data?.st_country_iso_code === timeZone)?.[0]

    return {
      matchedCountry,
      goToLabel: matchedCountry?.data?.st_country_label,
      goToLink: matchedCountry?.data?.st_country_link,
    }
  }

  private get cookieBannerHeadline () : string {
    return this.globalSettings?.data?.ps_country_project_settings?.pt_cookie_banner_headline || ''
  }

  private get cookieBannerText () : FSXARichText[] | undefined {
    return this.globalSettings?.data?.ps_country_project_settings?.pt_cookie_banner_text?.map(
      (text : Paragraph) => text?.content,
    )
  }

  private get cookieBannerLink () : ILink {
    return getLinkObject(
      this.globalSettings?.data?.ps_country_project_settings?.pt_cookie_banner_link,
      this.getUrlByPageId,
    )
  }

  private get showCookieRejectAllButton () : boolean {
    return !!this.globalSettings?.data?.ps_country_project_settings?.pt_cookie_banner_buttons?.some(
      (button : Option) => button?.key === 'reject_all',
    )
  }

  private get showCookieAcceptAllButton () : boolean {
    return !!this.globalSettings?.data?.ps_country_project_settings?.pt_cookie_banner_buttons?.some(
      (button : Option) => button?.key === 'accept_all',
    )
  }

  private get showCookieOkayButton () : boolean {
    return !!this.globalSettings?.data?.ps_country_project_settings?.pt_cookie_banner_buttons?.some(
      (button : Option) => button?.key === 'okay',
    )
  }

  private get modal () : Record<'open' | 'component' | 'props', any> {
    return {
      open: this.$store.state.ModalContent.isOpen,
      component: this.$store.state.ModalContent.component,
      props: this.$store.state.ModalContent.props,
    }
  }

  @Watch('modal.open')
  private onModalOpenChange (newVal : boolean) : void {
    const functionToCall = newVal ? 'add' : 'remove'
    document.body.classList[functionToCall]('overflow-hidden')
  }
}
